(function () {
  class MachineStructureComponent extends HTMLElement {
    constructor() {
      super();

      // Elements
      this.specsSection = null;
      this.imageWrapper = null;
      this.imageSticky = null;
      this.specsLeft = null;
      this.specsRight = null;
      this.accessoryVideos = [];
      this.playlistIndex = 0;
      this.playlistActive = false;
      this.accessoriesInView = false;
      this.accessoryObserver = null;
      this.onVisibilityChange = null;

      // Sticky scroll state
      this.scrollHandler = null;
      this.resizeHandler = null;

      this.init();
    }

    init() {
      this.setupElements();
      this.bindEvents();
      this.setupAccessoryPlaylist();
      this.syncWrapperHeight();
      this.handleStickyScroll(); // Initial calculation
    }

    setupElements() {
      // Get main elements
      this.specsSection = this.querySelector('[data-specs-section]');
      this.specsContainer = this.querySelector('.machine-structure__specs-container');
      this.imageWrapper = this.querySelector('[data-image-wrapper]');
      this.imageSticky = this.querySelector('[data-image-sticky]');
      this.specsLeft = this.querySelector('[data-specs-left]');
      this.specsRight = this.querySelector('[data-specs-right]');

      this.accessoryVideos = Array.from(this.querySelectorAll('[data-accessory-video]'));
    }

    bindEvents() {
      // Sticky scroll effect
      this.scrollHandler = this.throttle(this.handleStickyScroll.bind(this), 16);
      window.addEventListener('scroll', this.scrollHandler, { passive: true });

      // Resize
      this.resizeHandler = this.throttle(() => {
        this.syncWrapperHeight();
        this.handleStickyScroll();
      }, 200);
      window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * Play accessory videos one at a time once the accessories section is visible.
     */
    setupAccessoryPlaylist() {
      if (!this.accessoryVideos.length) return;

      this.accessoryVideos.forEach((video) => {
        video.loop = false;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        const onEnded = () => this.onAccessoryVideoEnded(video);
        video._accessoryEndedHandler = onEnded;
        video.addEventListener('ended', onEnded);
      });

      const section = this.querySelector('.machine-structure__accessories-section');
      if (section && 'IntersectionObserver' in window) {
        this.accessoryObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.accessoriesInView = entry.isIntersecting;
            if (entry.isIntersecting && !document.hidden) {
              this.startAccessoryPlaylist();
            } else {
              this.pauseAccessoryPlaylist();
            }
          });
        }, { threshold: 0 });
        this.accessoryObserver.observe(section);
      }

      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.pauseAccessoryPlaylist();
          return;
        }
        if (this.accessoriesInView) {
          this.startAccessoryPlaylist();
        }
      };
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    startAccessoryPlaylist() {
      if (!this.accessoryVideos.length || this.playlistActive) return;
      this.playlistActive = true;
      this.playCurrentAccessoryVideo();
    }

    pauseAccessoryPlaylist() {
      this.playlistActive = false;
      const video = this.accessoryVideos[this.playlistIndex];
      if (!video) return;

      try {
        video.pause();
      } catch (error) {
        console.error('Error pausing video:', error);
      }
    }

    playCurrentAccessoryVideo() {
      const video = this.accessoryVideos[this.playlistIndex];
      if (!video || !this.playlistActive) return;

      this.accessoryVideos.forEach((other, index) => {
        if (index === this.playlistIndex || other.paused) return;
        other.pause();
      });

      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (!this.playlistActive || this.accessoryVideos[this.playlistIndex] !== video) {
              video.pause();
            }
          })
          .catch((error) => {
            console.log('Video play prevented:', error);
          });
      }
    }

    onAccessoryVideoEnded(video) {
      if (!this.playlistActive) return;
      if (video !== this.accessoryVideos[this.playlistIndex]) return;

      try {
        video.pause();
        video.currentTime = 0;
      } catch (error) {
        console.error('Error resetting video:', error);
      }

      this.playlistIndex = (this.playlistIndex + 1) % this.accessoryVideos.length;
      this.playCurrentAccessoryVideo();
    }

    /**
     * Handle sticky scroll effect for machine image
     * The image should stick while specs are scrolling
     * When the bottom of specs aligns with bottom of image, everything scrolls together
     */
    handleStickyScroll() {
      if (!this.imageSticky || !this.specsLeft || !this.specsRight) return;

      // Skip on mobile
      if (window.innerWidth < 990) {
        this.imageSticky.style.top = '0';
        return;
      }

      const viewportHeight = window.innerHeight;
      const specsLeftRect = this.specsLeft.getBoundingClientRect();
      const specsRightRect = this.specsRight.getBoundingClientRect();
      const imageRect = this.imageSticky.getBoundingClientRect();

      // Get the bottom position of the taller specs column
      const specsBottomLeft = specsLeftRect.bottom;
      const specsBottomRight = specsRightRect.bottom;
      const specsBottom = Math.max(specsBottomLeft, specsBottomRight);

      // Calculate how much to adjust sticky position
      // Default sticky position is 20vh from top
      const defaultTop = viewportHeight * 0.2;
      const imageBottom = imageRect.top + imageRect.height;

      // When specs bottom is above image bottom, adjust sticky position
      // This creates the effect of image "waiting" for specs to catch up
      if (specsBottom < imageBottom) {
        const diff = imageBottom - specsBottom;
        const newTop = Math.max(0, defaultTop - diff);
        this.imageSticky.style.top = `${newTop}px`;
      } else {
        this.imageSticky.style.top = `${defaultTop}px`;
      }
    }

    /**
     * Throttle function to limit scroll event frequency
     */
    throttle(fn, limit) {
      let inThrottle = false;
      return function (...args) {
        if (inThrottle) return;
        inThrottle = true;
        fn.apply(this, args);
        setTimeout(() => {
          inThrottle = false;
        }, limit);
      };
    }

    disconnectedCallback() {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      if (this.accessoryObserver) {
        this.accessoryObserver.disconnect();
        this.accessoryObserver = null;
      }
      if (this.onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.onVisibilityChange = null;
      }
      this.playlistActive = false;
      this.accessoryVideos.forEach((video) => {
        video.pause();
        if (video._accessoryEndedHandler) {
          video.removeEventListener('ended', video._accessoryEndedHandler);
        }
      });
    }

    /**
     * 同步中心容器高度为两侧参数的最大值，确保sticky在两侧滚完前不释放
     */
    syncWrapperHeight() {
      if (!this.imageWrapper || !this.specsLeft || !this.specsRight) return;

      // 仅桌面端生效
      if (window.innerWidth < 990) {
        this.imageWrapper.style.minHeight = '';
        if (this.specsContainer) this.specsContainer.style.minHeight = '';
        if (this.specsLeft) this.specsLeft.style.minHeight = '';
        if (this.specsRight) this.specsRight.style.minHeight = '';
        return;
      }

      const leftHeight = this.specsLeft.offsetHeight;
      const rightHeight = this.specsRight.offsetHeight;
      const imageStickyHeight = this.imageSticky ? this.imageSticky.offsetHeight : 0;
      const targetHeight = Math.max(leftHeight, rightHeight, imageStickyHeight, 942);
      this.imageWrapper.style.minHeight = `${targetHeight}px`;
      if (this.specsContainer) this.specsContainer.style.minHeight = `${targetHeight}px`;
      if (this.specsLeft) this.specsLeft.style.minHeight = `${targetHeight}px`;
      if (this.specsRight) this.specsRight.style.minHeight = `${targetHeight}px`;
    }
  }

  customElements.define('machine-structure-component', MachineStructureComponent);

  // Handle back/forward cache
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      document.querySelectorAll('machine-structure-component').forEach((component) => {
        if (component.handleStickyScroll) {
          component.syncWrapperHeight && component.syncWrapperHeight();
          component.handleStickyScroll();
        }
        if (component.accessoriesInView && !document.hidden && component.startAccessoryPlaylist) {
          component.playlistActive = false;
          component.startAccessoryPlaylist();
        }
      });
    }
  });
})();
